import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import LandingPage from "@/components/landing/LandingPage";
import { getLandingPageBySlug, getClusterPagesForMatrix } from "@/lib/landing-pages.functions";

export const Route = createFileRoute("/$slug")({
  loader: async ({ params }) => {
    // 301 redirect: slug com acento -> sem acento
    const normalized = params.slug
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    if (normalized !== params.slug) {
      throw redirect({
        to: "/$slug",
        params: { slug: normalized },
        statusCode: 301,
      });
    }
    // 301 redirect: Jardins slug rename
    if (params.slug === "mao-de-obra-temporaria-construcao-civil-em-jardins-sao-paulo") {
      throw redirect({
        to: "/$slug",
        params: { slug: "mao-de-obra-temporaria-construcao-civil-no-jardins" },
        statusCode: 301,
      });
    }
    // 301 redirect: old slug pattern -> new slug pattern
    const OLD_PREFIX = "mao-de-obra-temporaria-em-";
    const NEW_PREFIX = "mao-de-obra-temporaria-construcao-civil-em-";
    if (params.slug.startsWith(OLD_PREFIX)) {
      const newSlug = NEW_PREFIX + params.slug.slice(OLD_PREFIX.length);
      throw redirect({ to: "/$slug", params: { slug: newSlug }, statusCode: 301 });
    }
    const page = await getLandingPageBySlug({ data: { slug: params.slug } });
    if (!page) throw notFound();
    const cluster = page.matrix_id
      ? await getClusterPagesForMatrix({
          data: {
            matrixId: page.matrix_id,
            excludeSlug: page.slug,
            currentCidade: page.cidade ?? null,
          },
        })
      : [];
    return { page, cluster };
  },
  head: ({ loaderData }) => {
    const page = loaderData?.page;
    if (!page) {
      return {
        meta: [{ title: "Página não encontrada | Selectus RH" }, { name: "robots", content: "noindex" }],
      };
    }
    const url = `https://lp.selectusrh.com/${page.slug}`;
    const description = page.meta_description ?? "";
    const matrix = (page as any).matrix ?? null;
    const isTerceirizacao =
      matrix?.segmento === "Terceirização de Mão de Obra" ||
      matrix?.nome === "Terceirização de Mão de Obra";
    const thumbUrl = isTerceirizacao
      ? `https://lp.selectusrh.com/thumb/thumb-${page.slug}.webp`
      : null;
    // Derive a human-readable city name from the slug when possible
    const CITY_PREFIX = "mao-de-obra-temporaria-construcao-civil-em-";
    const cityFromSlug = page.slug.startsWith(CITY_PREFIX)
      ? page.slug
          .slice(CITY_PREFIX.length)
          .split("-")
          .map((w: string) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w))
          .join(" ")
      : null;
    return {
      meta: [
        { title: page.title },
        { name: "description", content: description },
        { property: "og:title", content: page.title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "pt_BR" },
        { name: "twitter:title", content: page.title },
        { name: "twitter:description", content: description },
        ...(thumbUrl
          ? [
              { property: "og:image", content: thumbUrl },
              { property: "og:image:secure_url", content: thumbUrl },
              { property: "og:image:type", content: "image/webp" },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "630" },
              { property: "og:image:alt", content: page.title },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: thumbUrl },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Início", item: "https://lp.selectusrh.com/" },
                  { "@type": "ListItem", position: 2, name: page.title, item: url },
                ],
              },
              {
                "@type": "Service",
                name: page.title,
                description,
                serviceType: "Mão de obra temporária para construção civil",
                provider: { "@id": "https://lp.selectusrh.com/#organization" },
                areaServed: cityFromSlug
                  ? { "@type": "City", name: cityFromSlug, containedInPlace: { "@type": "State", name: "São Paulo" } }
                  : "Brasil",
                audience: { "@type": "BusinessAudience", audienceType: "Construtoras, incorporadoras e empreiteiras" },
                url,
              },
              {
                "@type": "WebPage",
                url,
                name: page.title,
                description,
                inLanguage: "pt-BR",
                isPartOf: { "@id": "https://lp.selectusrh.com/#website" },
                about: { "@id": "https://lp.selectusrh.com/#service" },
                speakable: {
                  "@type": "SpeakableSpecification",
                  cssSelector: ["h1", "h2", "[data-speakable]"],
                },
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A landing page que você procura não existe ou ainda não foi publicada.
        </p>
        <div className="mt-6">
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Voltar para o início
          </a>
        </div>
      </div>
    </div>
  ),
  component: LandingPageRoute,
});

function LandingPageRoute() {
  const { page, cluster } = Route.useLoaderData();
  return <LandingPage pageData={page} clusterPages={cluster} />;
}
